
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { Classname } from "@rivtn/noseur";
import Btn from "../common/Btn";
import { i18nManager } from "../../i18n";
import { CacheManager, Utils } from "../../../../../utils";
import styles from "../../assets/style.module.css";
import { IMember, IProfileWebsite } from "../../../../../services";
import LanguageDropdown from "../../../../../shared/LanguageDropdown";
import { i18nManager as globalI18nManager } from "../../../../../i18n";
import { activeMedia, isPageVisible, pagePath, splitName, visiblePages } from "../../helpers";

export interface NavbarProps {
    basePath: string;
    cartCount: number;
    profile?: IMember;
    website: IProfileWebsite;
    onSignOut: () => void;
}

function Navbar({ website, basePath, profile, cartCount, onSignOut }: NavbarProps) {

    const labels = i18nManager.Labels;
    const authLabels = globalI18nManager.Labels.auth;
    const landingLabels = globalI18nManager.Labels.landing;
    const ddRef = React.useRef<HTMLDivElement>(null);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [searchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const { last } = splitName(website.display_name);
    const logo = activeMedia(website, "LOGO");
    const dashboardUrl = process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901";

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ddRef.current && !ddRef.current.contains(e.target as Node)) setShowDropdown(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const links = (onClick?: () => void) => visiblePages(website).map(([page, pageSettings]) => (
        <ReactRouterDom.NavLink end key={page} onClick={onClick} to={{ pathname: pagePath(basePath, page), search: searchParams.toString() }}
            className={({ isActive }) => Classname.build(styles["menu-link"], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
    ));

    return (<>
        <div className={styles["topbar"]}>
            <div className={Classname.build(styles["container"], styles["topbar-inner"])}>
                <div className={styles["topbar-socials"]}>
                    {Object.entries(website.member.social_accounts ?? {}).map(([social, username]) => (
                        <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} aria-label={social}><i className={Utils.getSocialIcon(social)} /></ReactRouterDom.NavLink>
                    ))}
                </div>
                <div className={styles["topbar-right"]}>
                    <LanguageDropdown />
                    {cartCount > 0 && (<span className={styles["cart"]}><i className="fa fa-cart-shopping" /> {cartCount}</span>)}
                    {(website.extra_data?.enable_visitor_account_management) && ((!profile) ? (
                        <ReactRouterDom.NavLink className={styles["signin"]} to={process.env.REACT_APP_PATULCIUS_AUTH_URL ?? "http://127.0.0.1:3005/oauth"}>{authLabels.sign_in}</ReactRouterDom.NavLink>
                    ) : (
                        <span className={styles["signin"]}>@{profile.username}</span>
                    ))}
                </div>
            </div>
        </div>

        <header className={styles["hdr"]}>
            <div className={Classname.build(styles["container"], styles["hdr-inner"])}>
                <ReactRouterDom.NavLink to={{ pathname: `/${basePath}`, search: searchParams.toString() }} className={styles["brand"]}>
                    {logo && (<img className={styles["brand-logo"]} src={logo} alt={website.display_name} />)}
                    <span>{last}</span>
                </ReactRouterDom.NavLink>

                <nav className={styles["menu"]}>{links()}</nav>

                <div className={styles["hdr-actions"]}>
                    {isPageVisible(website, "MERCH") && (<span className={styles["hdr-desktop"]}>
                        <Btn size="sm" to={{ pathname: pagePath(basePath, "MERCH"), search: searchParams.toString() }}>{labels.official_store}</Btn>
                    </span>)}
                    {profile && (<div className={styles["dropdown-wrap"]} ref={ddRef}>
                        <img className={styles["avatar"]} onClick={() => setShowDropdown((v) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                        {showDropdown && (<div className={styles["dropdown"]}>
                            <ReactRouterDom.NavLink target="_blank" className={styles["dd-item"]} to={dashboardUrl} onClick={() => setShowDropdown(false)}><i className="fa fa-chart-line" /> {authLabels.dashboard}</ReactRouterDom.NavLink>
                            <ReactRouterDom.NavLink target="_blank" className={styles["dd-item"]} to={dashboardUrl} onClick={() => setShowDropdown(false)}><i className="fa fa-gear" /> {landingLabels.my_tournaments}</ReactRouterDom.NavLink>
                            <ReactRouterDom.NavLink target="_blank" className={styles["dd-item"]} to={"https://omnehost.com/tournaments"} onClick={() => setShowDropdown(false)}><i className="fa fa-trophy" /> {landingLabels.join_tournaments}</ReactRouterDom.NavLink>
                            <hr className={styles["dd-divider"]} />
                            <button className={Classname.build(styles["dd-item"], styles["logout"])} onClick={() => {
                                CacheManager.clear();
                                setShowDropdown(false);
                                onSignOut();
                            }}><i className="fa fa-right-from-bracket" /> {authLabels.sign_out}</button>
                        </div>)}
                    </div>)}
                    <button className={Classname.build(styles["burger"], { [styles["open"]]: mobileOpen })} onClick={() => setMobileOpen((o) => !o)} aria-label={labels.menu}>
                        <span /><span /><span />
                    </button>
                </div>
            </div>
        </header>

        {mobileOpen && (<nav className={styles["drawer"]}>
            {links(() => setMobileOpen(false))}
        </nav>)}
    </>);
}

export default Navbar;
